import { TimestampTooltip } from '@/components/common/TimestampTooltip'
import { StatusIcon } from '@/components/network-status/SignalBars'
import {
	type ConnectionStatus,
	type NetworkPing,
	type NetworkTier,
	STATUS_LABEL,
	STATUS_TONE,
	TONE,
	connectionStatus,
	downloadTier,
	pingTier,
	tierBars,
} from '@/components/network-status/networkTier'
import type { SpeedTestControls, SpeedTestPhase } from '@/components/network-status/useSpeedTest'
import { Button } from '@/components/ui/button'
import { InlineHelpTooltip } from '@/components/ui/inline-help-tooltip'
import { PopoverContent } from '@/components/ui/popover'
import { Spinner } from '@/components/ui/spinner'
import { StateIcon } from '@/components/ui/state-icon'
import { branding } from '@/config/branding'
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import { formatBytes } from '@/utils/formatBytes'
import { CircleAlert, Gauge, type LucideIcon, RefreshCw, WifiOff } from 'lucide-react'
import type { ReactNode } from 'react'

interface NetworkSpeedPopoverProps {
	online: boolean
	ping: NetworkPing | undefined
	test: SpeedTestControls
}

const METER_STEPS = [1, 2, 3, 4]

const DATA_NOTE = 'Uses 100 to 800 MB of data.'

const BANDWIDTH = [
	{ direction: 'download', label: 'Download' },
	{ direction: 'upload', label: 'Upload' },
] as const

const START_ACTION: Record<Exclude<SpeedTestPhase, 'running'>, { label: string; icon: LucideIcon; testId: string }> = {
	idle: { label: 'Test speed', icon: Gauge, testId: 'network-speed-start' },
	done: { label: 'Test again', icon: RefreshCw, testId: 'network-speed-restart' },
	error: { label: 'Try again', icon: RefreshCw, testId: 'network-speed-retry' },
}

interface Metric {
	format: (value: number) => string
	unit: string
	tierOf: (value: number) => NetworkTier
}

const PING: Metric = { format: ms => String(Math.round(ms)), unit: 'ms', tierOf: pingTier }

const BANDWIDTH_METRIC: Metric = {
	format: bps => {
		const mbps = bps / 1e6
		return mbps >= 10 ? String(Math.round(mbps)) : mbps.toFixed(1)
	},
	unit: 'Mbps',
	tierOf: downloadTier,
}

const StatusChip = ({ status }: { status: ConnectionStatus }) => (
	<output
		data-testid='network-speed-tier'
		data-tier={status}
		className={cn(
			'inline-flex h-6 w-fit shrink-0 items-center gap-1.5 rounded-md px-2 text-body font-semibold',
			STATUS_TONE[status].chip,
		)}>
		<StatusIcon status={status} />
		{STATUS_LABEL[status]}
	</output>
)

interface MetricSectionProps {
	label: string
	aside?: ReactNode
	columns: 2 | 3
	after?: ReactNode
	children: ReactNode
}

/** A labelled row of metric tiles in one divided box. */
const MetricSection = ({ label, aside, columns, after, children }: MetricSectionProps) => (
	<section className='flex flex-col gap-2' aria-label={label}>
		<div className='flex items-center justify-between'>
			<span className='text-control font-medium text-muted-foreground'>{label}</span>
			{aside}
		</div>
		<div className={cn('grid gap-y-1.5 divide-x rounded-md border', columns === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
			{children}
		</div>
		{after}
	</section>
)

interface TileProps {
	label: ReactNode
	value: number | undefined
	metric: Metric
	/** Shown when there is no value because the measurement failed, rather than not having run. */
	failure?: string
	paused?: boolean
	testId: string
}

// A subgrid row per part, so labels, values and meters line up across tiles even when a label is taller.
const Tile = ({ label, value, metric, failure, paused = false, testId }: TileProps) => {
	const tier = value === undefined || paused ? undefined : metric.tierOf(value)
	let reading = <span className='text-base font-semibold text-muted-foreground'>-</span>
	if (value !== undefined) {
		reading = (
			<span className='flex items-baseline gap-1'>
				<span className={cn('text-base font-semibold tabular-nums', paused && 'text-muted-foreground')}>
					{metric.format(value)}
				</span>
				<span className='text-control text-muted-foreground'>{metric.unit}</span>
			</span>
		)
	} else if (failure) {
		reading = <span className='self-center text-control text-destructive'>{failure}</span>
	}
	return (
		<div className='row-span-3 grid min-w-0 grid-rows-subgrid px-3 py-2.5'>
			<span className='flex min-w-0 items-center gap-1 truncate text-control text-muted-foreground'>{label}</span>
			<span data-testid={testId} className='flex'>
				{reading}
			</span>
			<div aria-hidden className='grid grid-cols-4 gap-0.5 self-end'>
				{METER_STEPS.map(step => (
					<span
						key={step}
						className={cn('h-1 rounded-full', tier && step <= tierBars(tier) ? STATUS_TONE[tier].fill : 'bg-muted')}
					/>
				))}
			</div>
		</div>
	)
}

const OfflineBody = () => (
	<div data-testid='network-speed-offline' className='flex flex-col items-center gap-2.5 py-6 text-center'>
		<StateIcon icon={WifiOff} tone='destructive' />
		<span className='text-state-title font-semibold'>You're offline</span>
		<span className='text-body text-muted-foreground'>Reconnect to measure your connection.</span>
	</div>
)

const NetworkSpeedBody = ({ ping, test }: Omit<NetworkSpeedPopoverProps, 'online'>) => {
	const running = test.phase === 'running'
	const unreachable = ping === undefined ? undefined : 'Unreachable'

	let actions: ReactNode
	if (test.phase === 'running') {
		actions = (
			<>
				<span className='flex flex-col gap-0.5'>
					<span className='flex items-center gap-1.5 text-control font-medium text-primary'>
						<Spinner className='size-3' />
						Testing {test.direction}
					</span>
					<span className='text-control text-muted-foreground'>{DATA_NOTE}</span>
				</span>
				<Button variant='outline' onClick={test.stop} data-testid='network-speed-stop'>
					Stop
				</Button>
			</>
		)
	} else {
		const { label, icon: Icon, testId } = START_ACTION[test.phase]
		const startButton = (
			<Button variant='outline' onClick={test.start} data-testid={testId}>
				<Icon className='text-primary' aria-hidden />
				{label}
			</Button>
		)
		actions =
			test.phase === 'error' ? (
				<div
					role='alert'
					data-testid='network-speed-error'
					className={cn('flex w-full items-center gap-2 rounded-md px-2.5 py-2', TONE.danger.chip)}>
					<CircleAlert className='size-(--icon-standalone) shrink-0' aria-hidden />
					<span className='flex-1 text-control'>
						Couldn't reach the speed test server. A firewall or VPN may block it.
					</span>
					{startButton}
				</div>
			) : (
				<>
					<span className='text-control text-muted-foreground'>
						{test.phase === 'done' ? (
							<>
								Tested{' '}
								<TimestampTooltip value={test.finishedAt} dataTestId='network-speed-tested-at'>
									{dayjs(test.finishedAt).fromNow()}
								</TimestampTooltip>
								, used {formatBytes(test.usedBytes)}.
							</>
						) : (
							DATA_NOTE
						)}
					</span>
					{startButton}
				</>
			)
	}

	return (
		<>
			<MetricSection
				label='Ping'
				columns={3}
				aside={
					running ? (
						<span className='text-control text-muted-foreground'>Paused during test</span>
					) : (
						<span data-testid='network-speed-live' className='flex items-center gap-1.5 text-control text-success'>
							<span className='size-1.5 animate-pulse rounded-full bg-success' aria-hidden />
							Live
						</span>
					)
				}>
				<Tile
					label='Internet'
					value={ping?.internetMs}
					metric={PING}
					failure={unreachable}
					paused={running}
					testId='network-speed-ping-internet'
				/>
				<Tile
					label={branding.companyName}
					value={ping?.bytebeamMs}
					metric={PING}
					failure={unreachable}
					paused={running}
					testId='network-speed-ping-bytebeam'
				/>
				<Tile
					label={
						<>
							Under load
							<InlineHelpTooltip
								label='About ping under load'
								content='Internet ping while the test fills your line. A big jump over idle ping makes live views lag.'
								dataTestId='network-speed-loaded-latency-help'
							/>
						</>
					}
					value={test.loadedLatencyMs}
					metric={PING}
					testId='network-speed-loaded-latency'
				/>
			</MetricSection>

			<MetricSection
				label='Speed'
				columns={2}
				after={<div className='flex items-center justify-between gap-3 pt-1'>{actions}</div>}>
				{BANDWIDTH.map(({ direction, label }) => (
					<Tile
						key={direction}
						label={label}
						value={test[`${direction}Bps`]}
						metric={BANDWIDTH_METRIC}
						testId={`network-speed-${direction}`}
					/>
				))}
			</MetricSection>
		</>
	)
}

/** Content for a `Popover` the caller owns. */
export function NetworkSpeedPopover({ online, ping, test }: NetworkSpeedPopoverProps) {
	const status = connectionStatus(online, ping, test.phase === 'done' ? test.downloadBps : undefined)
	return (
		<PopoverContent
			side='top'
			align='start'
			sideOffset={8}
			aria-label='Network speed'
			className='z-100 flex w-110 flex-col gap-5 p-5'
			// Focus the panel, not its first tab stop: that is a help tooltip button and would open on arrival.
			onOpenAutoFocus={e => {
				e.preventDefault()
				;(e.currentTarget as HTMLElement).focus()
			}}
			data-testid='network-speed-popover'>
			<div className='flex items-center justify-between gap-3'>
				<h2 className='text-form-modal-title leading-none font-bold'>Network speed</h2>
				<StatusChip status={status} />
			</div>
			{online ? <NetworkSpeedBody ping={ping} test={test} /> : <OfflineBody />}
		</PopoverContent>
	)
}
